import { ComponentType } from 'react';
import dynamic from 'next/dynamic';
import { Widget, WidgetType } from '../../../apiService';
import type { TeaserHeaderInfo } from './TeaserVideoWidget/TeaserVideo';
import { WidgetWrapper } from './WidgetWrapper';
import FranchiseWidget from './FranchiseWidget';
import CarouselWidget from './CarouselWidget';
import PosterWidget from './PosterWidget';
import PromotedWidget from './PromotedWidget';
import CategoryWidget from './CategoryWidget';
import ArtistWidget from './ArtistWidget';
import VitrineWidget from './VitrineWidget';
import TeaserVideoWidget from './TeaserVideoWidget';
import LiveCarouselWidget from './LiveCarouselWidget';
import ContinueWatchingWidget from './ContinueWatchingWidget';
import MainSliderWidget from './MainSliderWidget';

const ExclusiveWidget = dynamic(() => import('./ExclusiveWidget'));

type WidgetProps = {
  widget: Widget;
  headerInfo?: TeaserHeaderInfo;
  afterTeaser?: boolean;
  // First thing on the page, right under the header. Only the slider uses it.
  atPageTop?: boolean;
  eager?: boolean;
  serverTime?: string;
};

const getWidgetType = (type: WidgetType): ComponentType<WidgetProps> | null => {
  switch (type) {
    case 'main_slider': return MainSliderWidget;
    case 'franchise': return FranchiseWidget;
    case 'regular_carousel':
    case 'promoted_carousel': return CarouselWidget;
    case 'poster': return PosterWidget;
    case 'promoted': return PromotedWidget;
    case 'category_carousel': return CategoryWidget;
    case 'artist_carousel': return ArtistWidget;
    case 'vitrine': return VitrineWidget;
    case 'exclusive': return ExclusiveWidget;
    case 'teaser_video': return TeaserVideoWidget;
    case 'live_channel': return LiveCarouselWidget;
    case 'continue_watching': return ContinueWatchingWidget;
    default: return null;
  }
};

type WidgetItemProps = WidgetProps & { afterSlider?: boolean };

const WidgetItem = ({ widget, headerInfo, afterTeaser, afterSlider, atPageTop, eager, serverTime }: WidgetItemProps) => {
  const Component = widget.content_category_type ? getWidgetType(widget.content_category_type) : getWidgetType(widget.type);
  if (!Component) return null;

  // Poster and main_slider are full-bleed; cancel Layout horizontal padding.
  const cancelPadding = widget.type === 'poster' || widget.type === 'main_slider';
  // The slider doesn't overlap the teaser, so it needn't out-stack it. Raising it would bury the
  // row under it, which its negative bottom margin pulls up onto the slider, cutting off that row's top.
  const raiseOverTeaser = afterTeaser && widget.type !== 'main_slider';
  return (
    <WidgetWrapper cancelPadding={cancelPadding} afterTeaser={raiseOverTeaser} afterSlider={afterSlider}>
      <Component
        widget={widget}
        headerInfo={headerInfo}
        afterTeaser={afterTeaser}
        atPageTop={atPageTop}
        eager={eager}
        serverTime={serverTime}
      />
    </WidgetWrapper>
  );
};

export default WidgetItem;
